import { useCallback, useMemo } from 'react';
import { availabilityRelationsSelector } from 'slices/availabilityRelation.slice';
import {
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  getAvailabilityRelatedTypes,
  getAvailabilityRelationChange,
} from 'utils/associations';
import type { TTEObject } from '@timeedit/types';
import type { ObjectManagerDetailProps } from '../ObjectManagerDetail';
import { Association } from './Association';

const AvailabilityRelated = ({
  object,
  onChange,
  dirtyFields,
}: ObjectManagerDetailProps) => {
  const { availabilityRelations, types } = object;

  const allAvailabilityRelations = useAppSelector(
    availabilityRelationsSelector
  );
  const typeMap = useAppSelector(selectObjectTypesMap);
  const typeTree = useAppSelector(selectObjectTypesTree);

  const selectedObjectIds = useMemo(
    () => availabilityRelations.flatMap((ar) => ar?.objectIds),
    [availabilityRelations]
  );

  const availabilityTypes = useMemo(
    () =>
      getAvailabilityRelatedTypes(
        types,
        typeTree,
        allAvailabilityRelations,
        typeMap
      ),
    [types, typeTree, allAvailabilityRelations, typeMap]
  );

  const onRemove = useCallback(
    (objects: TTEObject[]) => {
      const newArs = availabilityRelations
        .map((ar) => {
          const newObjectIds = ar.objectIds.filter((objectId) =>
            objects.every((object) => object.id !== objectId)
          );
          return { ...ar, objectIds: newObjectIds };
        })
        .filter((ar) => ar.objectIds.length > 0);
      onChange({ availabilityRelations: newArs });
    },
    [availabilityRelations, onChange]
  );

  const onSelectedObjectsChange = useCallback(
    (addedObjects: TTEObject[]) => {
      getAvailabilityRelationChange(
        addedObjects,
        availabilityRelations,
        allAvailabilityRelations,
        typeTree,
        types,
        onChange
      );
    },
    [availabilityRelations, allAvailabilityRelations, typeTree, types, onChange]
  );

  return (
    <Association
      tableId="objects.availabilityRelated"
      objectIds={selectedObjectIds}
      objectTypes={availabilityTypes}
      onChange={onSelectedObjectsChange}
      onRemove={onRemove}
      data-testid="avail-relation"
      changedIds={dirtyFields?.availabilityRelations}
    />
  );
};

export default AvailabilityRelated;
